import { useState, type ReactNode } from "react";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { mapApiError } from "../ui/StatusMessage";
import { useToast } from "../ui/Toast";
import { api } from "../../lib/api";
import type { ChapterGroupCounts } from "../../lib/ingestChapterApi";
import type { ChapterMode } from "../../lib/ingestReviewApi";

type Props = {
  token: string;
  courseId: string;
  order: number;
  title: string;
  counts?: ChapterGroupCounts;
  shown: number;
  mode: ChapterMode;
  canApprove: boolean;
  onDone: () => void;
  children: ReactNode;
};

/** One collapsible chapter of the review list, with its topic counts and "Approve all topics in this chapter". */
export function IngestChapterGroup({ token, courseId, order, title, counts, shown, mode, canApprove, onDone, children }: Props) {
  const { showToast } = useToast();
  const [busy, setBusy] = useState(false);
  const approve = async () => {
    setBusy(true);
    try {
      const r = await api.admin.ingestReview.approveChapter(token, { course_id: courseId, chapter_order: order, on_conflict: mode === "replace" ? "append" : mode });
      r.warnings.forEach((w) => showToast("info", w));
      showToast(r.skipped ? "info" : "success", `${r.approved} approved, ${r.skipped} skipped`);
      onDone();
    } catch (e) { showToast("error", mapApiError(e).message); } finally { setBusy(false); }
  };
  return (
    <details open className="space-y-3">
      <summary className="flex min-h-11 cursor-pointer flex-wrap items-center gap-2 text-base font-semibold">
        <span>Chapter {order}: {title}</span>
        <span className="text-sm font-normal text-muted-foreground">{counts?.total ?? shown} topic{(counts?.total ?? shown) === 1 ? "" : "s"}</span>
        {!!counts?.needs_fixing && <Badge variant="warning">{counts.needs_fixing} need fixing</Badge>}
      </summary>
      {canApprove && (
        <Button size="sm" variant="outline" loading={busy} onClick={() => void approve()}>Approve all topics in this chapter</Button>
      )}
      <div className="space-y-4">{children}</div>
    </details>
  );
}
